import React, {createContext, useContext, useState, useEffect} from 'react';
import { toast } from 'react-toastify';
import useRequest from 'hooks/useRequest';
import { useNavigation } from 'context/NavigationContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';

const BasketContext = createContext();

export const BasketProvider = ({ children }) => {
    const [basketItems, setBasketItems] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [productsPrice, setProductsPrice] = useState(0);
    const [fees, setFees] = useState(0);
    const [tip, setTip] = useState(0);
    const [prescriptionInfo, setPrescriptionInfo] = useState(null);
    const { sendRequest } = useRequest();
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation();
    const { navigate } = useNavigation();
    const { user, hasToken } = useAuth();

    // Fetch basket data on mount only if authenticated
    useEffect(() => {
        const fetchBasket = async () => {
            // Only fetch basket if user is authenticated
            if (!hasToken() || !user) {
                setIsLoading(false);
                return;
            }
            
            try {
                const response = await sendRequest({
                    method: 'POST',
                    url: `module/getBasket/basket`,
                    data: {orderType: 0}
                });
                if (response.status === 'success') {
                    setBasketItems(response.data.products || []);
                    setTotalPrice(response.data.totalPrice || 0);
                    setProductsPrice(response.data.productsPrice || 0);
                    setFees(response.data.fees?.totalFees || 0);
                }
            } catch (error) {
                console.error('Error fetching basket:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBasket();
    }, [user, hasToken]);

    const deliveryCost = 2;

    const addToBasket = async(product) => {
        try {
            const response = await sendRequest({
                method: 'POST',
                url: `module/editBasket/basket`,
                data: {products: [{integrationId: product.integrationId || product.productIntegrationId, quantity: (product.quantity || 1)}]}
            });
            
            if (response.status === 'success') {
                // Always refresh basket from server to ensure consistency
                await refreshBasket();
                
                // Show interactive toast
                toast.success(
                    <div className="flex items-center justify-between w-full">
                        <span>{t('component.basket.toast.added_to_basket')}</span>
                        <button 
                            className="text-sm text-blue-600 hover:text-blue-800 underline"
                            onClick={() => navigate('/basket')}
                        >
                            {t('component.basket.toast.view_basket')}
                        </button>
                    </div>,
                    {
                        autoClose: 3000,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        className: 'cursor-default'
                    }
                );
                return { status: 'success', data: response.data };
            } else {
                throw new Error(response.message || 'Failed to add product');
            }
        } catch (error) {
            console.error('Error adding to basket:', error);
            toast.error(t('component.basket.toast.failed_to_add'));
            // Refresh basket to ensure we have latest state even on error
            await refreshBasket();
            return { status: 'error', error: error.message };
        }
    };

    const removeFromBasket = async (productUuid) => {
        try {
            const response = await sendRequest({
                method: 'POST',
                url: `module/removeProductOfBasket/basket`,
                data: { productUuid: productUuid }
            });
            
            if (response.status === 'success') {
                // Optimistically update local state for better UX
                const newItems = basketItems.filter(item => item.productIntegrationId !== productUuid);
                setBasketItems(newItems);
                
                // Then refresh from server to ensure consistency
                await refreshBasket();
                
                return { status: 'success', data: response.data };
            } else {
                throw new Error(response.message || 'Failed to remove product');
            }
        } catch (error) {
            console.error('Error removing from basket:', error);
            toast.error(t('component.basket.toast.error_removing'));
            // Refresh basket to ensure we have latest state even on error
            await refreshBasket();
            throw error;
        }
    };

    const updateQuantity = async (productUuid, quantity) => {
        try {
            if (quantity === 0) {
                return removeFromBasket(productUuid);
            }

            const response = await sendRequest({
                method: 'POST',
                url: `module/editBasket/basket`,
                data: {products: [{integrationId: productUuid, quantity: quantity}]}
            });
            
            if (response.status === 'success') {
                // Optimistically update local state for better UX
                const updatedItems = basketItems.map(item => 
                    item.productIntegrationId === productUuid 
                        ? { ...item, quantity: quantity }
                        : item
                );
                setBasketItems(updatedItems);
                
                // Then refresh from server to ensure consistency
                await refreshBasket();
                
                return { status: 'success', data: response.data };
            } else {
                throw new Error(response.message || 'Failed to update quantity');
            }
        } catch (error) {
            console.error('Error updating quantity:', error);
            toast.error(t('component.basket.toast.error_updating'));
            // Refresh basket to ensure we have latest state even on error
            await refreshBasket();
            throw error;
        }
    };

    const clearBasket = async () => {
        try {
            // Remove all items by setting their quantity to 0
            const products = basketItems.map(item => ({
                integrationId: item.productIntegrationId,
                quantity: 0
            }));

            const response = await sendRequest({
                method: 'POST',
                url: `module/editBasket/basket`,
                data: { products: products }
            });

            if (response.status === 'success') {
                // Clear local state immediately
                setBasketItems([]);
                setTotalPrice(0);
                setProductsPrice(0);
                setFees(0);
                setTip(0);

                // Then refresh from server to ensure consistency
                await refreshBasket();

                return { status: 'success', data: response.data };
            } else {
                throw new Error(response.message || 'Failed to clear basket');
            }
        } catch (error) {
            console.error('Error clearing basket:', error);
            toast.error(t('component.basket.toast.error_clearing'));
            // Refresh basket to ensure we have latest state even on error
            await refreshBasket();
            throw error;
        }
    };

    const refreshBasket = async (orderType = 0) => {
        // Only refresh if authenticated
        if (!hasToken() || !user) {
            return {
                products: basketItems,
                totalPrice,
                productsPrice,
                fees: { totalFees: fees }
            };
        }
        
        try {
            const response = await sendRequest({
                method: 'POST',
                url: `module/getBasket/basket`,
                data: {orderType: orderType}
            });
            if (response.status === 'success') {
                setBasketItems(response.data.products || []);
                setTotalPrice(response.data.totalPrice || 0);
                setProductsPrice(response.data.productsPrice || 0);
                setFees(response.data.fees?.totalFees || 0);
                return response.data;
            }
        } catch (error) {
            console.error('Error refreshing basket:', error);
            // Return current state on error
            return {
                products: basketItems,
                totalPrice,
                productsPrice,
                fees: { totalFees: fees }
            };
        }
    };

    const getBasketForOrderType = async (orderType) => {
        // Only fetch if authenticated
        if (!hasToken() || !user) {
            return {
                products: basketItems,
                totalPrice,
                productsPrice,
                fees: { totalFees: fees }
            };
        }
        
        try {
            const response = await sendRequest({
                method: 'POST',
                url: `module/getBasket/basket`,
                data: {orderType: orderType}
            });
            if (response.status === 'success') {
                setBasketItems(response.data.products || []);
                setTotalPrice(response.data.totalPrice || 0);
                setProductsPrice(response.data.productsPrice || 0);
                setFees(response.data.fees?.totalFees || 0);
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching basket for order type:', error);
            // Return current state on error
            return {
                products: basketItems,
                totalPrice,
                productsPrice,
                fees: { totalFees: fees }
            };
        }
    };

    const fetchInitialFees = async () => {
        // Only fetch if authenticated
        if (!hasToken() || !user) {
            return {
                bringzz: {
                    totalFees: 0
                },
                collect: {
                    totalFees: 0
                },
                shipment: {
                    totalFees: 0
                }
            };
        }
        
        try {
            const response = await sendRequest({
                method: 'GET',
                url: `module/getFees/basket`
            });
            if (response.status === 'success') {
                console.log(response.data);
                return {
                    bringzz: {
                        totalFees: response.data.bringzz?.totalFees
                    },
                    collect: {
                        totalFees: response.data.collect?.totalFees
                    },
                    shipment: {
                        totalFees: response.data.shipment?.totalFees
                    }
                };
            }
        } catch (error) {
            console.error('Error fetching basket for order type:', error);
            // Return current state on error
            return {
                bringzz: {
                    totalFees: 0
                },
                collect: {
                    totalFees: 0
                },
                shipment: {
                    totalFees: 0
                }
            };
        }
    };

    const getBasketTotal = () => {
        return (parseFloat(totalPrice) + parseFloat(tip || 0)).toFixed(2);
    };

    const getBasketSubTotal = () => {
        return productsPrice;
    };

    // To be deleted
    const getDeliveryCost = () => {
        return deliveryCost.toFixed(2);
    };

    const getFees = () => {
        return fees;
    };

    const setBasketTip = (amount) => {
        const tipAmount = parseFloat(amount) || 0;
        setTip(tipAmount);
    };

    const getBasketTip = () => {
        return tip;
    };

    const savePrescriptionInfo = (prescriptionData) => {
        if (prescriptionData && prescriptionData.scan_results) {
            const info = {
                name: prescriptionData.scan_results.name,
                svnr: prescriptionData.scan_results.svnr,
                rezId: prescriptionData.scan_results.rez_id,
                eMedId: prescriptionData.scan_results.e_med_id,
                issuedDate: prescriptionData.scan_results.issued_date,
                expiredDate: prescriptionData.scan_results.expired_date,
                prescriptionFeeExemption: prescriptionData.scan_results.prescription_fee_exemption,
                address: prescriptionData.scan_results.address_1
            };
            setPrescriptionInfo(info);
            // Also persist to localStorage for session persistence
            localStorage.setItem('prescriptionInfo', JSON.stringify(info));
        }
    };

    const clearPrescriptionInfo = () => {
        setPrescriptionInfo(null);
        localStorage.removeItem('prescriptionInfo');
    };

    // Load prescription info from localStorage on mount
    useEffect(() => {
        const storedPrescription = localStorage.getItem('prescriptionInfo');
        if (storedPrescription) {
            try {
                setPrescriptionInfo(JSON.parse(storedPrescription));
            } catch (error) {
                console.error('Error parsing stored prescription info:', error);
            }
        }
    }, []);

    // Clear basket when user logs out
    useEffect(() => {
        if (!user || !hasToken()) {
            setBasketItems([]);
            setTotalPrice(0);
            setProductsPrice(0);
            setFees(0);
            setTip(0);
        }
    }, [user, hasToken]);

    // Force refresh basket (manual refresh)
    const forceRefreshBasket = async () => {
        return await refreshBasket();
    };

    return (
        <BasketContext.Provider value={{
            basketItems,
            prescriptionInfo,
            addToBasket,
            removeFromBasket,
            updateQuantity,
            clearBasket,
            refreshBasket,
            forceRefreshBasket,
            getBasketTotal,
            getBasketSubTotal,
            getDeliveryCost,
            getFees,
            getBasketForOrderType,
            fetchInitialFees,
            savePrescriptionInfo,
            clearPrescriptionInfo,
            setBasketTip,
            getBasketTip,
            isLoading
        }}>
            {children}
        </BasketContext.Provider>
    );
};

export const useBasket = () => useContext(BasketContext);
